/**
 * The day, read from the database rather than counted in the browser.
 *
 * `worklist_summary` computes every group over the caller's **whole** visible
 * book, using the same `book_follow_up_category()` that `book_matches()` uses
 * — so a card's figure and the book filter it links to are the same question,
 * and `supabase/tests/12` asserts exactly that against `book_totals()`.
 *
 * The reference implementation's `worklistSummary()` takes an array of
 * customers and adds them up. That is the right shape for a small list already
 * on screen and the wrong shape for this: a morning screen that counted the
 * first page would tell a collector with 1,800 accounts that they have nothing
 * to do, and would be most wrong for the people with the most work.
 *
 * Everything here is `security invoker`, so a collector's day is their own
 * accounts and a manager's is the book they may see. No client-side filtering
 * stands anywhere in it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    DEFAULT_FILTERS, WORKLIST_GROUPS, readWorklistSummary,
    type TenantContext, type WorklistFigure, type WorklistGroup, type WorklistTotals,
} from '@timely/domain';
import { supabase } from '../lib/supabaseClient';
import { emptyChequeTotals, type ChequeTotals } from '../cheques/useCheques';
import { filterArgs } from './useBook';
import { readAgeingReport, type AgeingReportRow } from '../receivables/ageingReport';

/** One colleague's share of the work, for a manager's Today. */
export interface MemberWorkload {
    memberId: string;
    displayName: string;
    overdueCount: number;
    todayCount: number;
    urgentCount: number;
    openCount: number;
    openAmount: number;
    /** What is planned (0033), so the panel can show each person's coverage. */
    futureCount: number;
    noFollowUpCount: number;
    completedCount: number;
    /** Marked as bad debt, and so outside the queue and the plan. */
    badDebtCount: number;
}

/**
 * One row of this person's visible book's ageing (`report_ageing`), for Today's
 * glance: a sheet's band, or a TallyPrime company's own window (D3).
 */
export type TodayAgeingBand = AgeingReportRow;

export interface TodayData {
    totals: WorklistTotals | null;
    team: MemberWorkload[];
    cheques: ChequeTotals | null;
    /** True when the cheque figures were refused, not merely empty. */
    chequesUnavailable: boolean;
    ageing: TodayAgeingBand[];
}

const EMPTY: TodayData = { totals: null, team: [], cheques: null, chequesUnavailable: false, ageing: [] };

export function useToday(context: TenantContext, options: { withTeam: boolean; withCheques: boolean; withAgeing?: boolean }) {
    const workspaceId = context.workspace.id;
    const [data, setData] = useState<TodayData>(EMPTY);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const { withTeam, withCheques } = options;
    const withAgeing = options.withAgeing ?? false;
    const key = JSON.stringify([workspaceId, withTeam, withCheques, withAgeing]);
    const issuedFor = useRef(key);

    const load = useCallback(async () => {
        issuedFor.current = key;
        setLoading(true);
        setError(null);

        const client = supabase();
        const [summary, team, cheques, ageing] = await Promise.all([
            client.rpc('worklist_summary', { p_workspace: workspaceId }),
            withTeam
                ? client.rpc('worklist_by_member', { p_workspace: workspaceId })
                : Promise.resolve({ data: null, error: null }),
            // The same call the register makes with no filters, so Today's
            // cheque figures and the register's tiles cannot disagree.
            withCheques
                ? client.rpc('cheque_totals', { p_workspace: workspaceId })
                : Promise.resolve({ data: null, error: null }),
            // The report's own ageing, so Today's bar and the report's table are one figure.
            withAgeing
                ? client.rpc('report_ageing', { p_workspace: workspaceId, p_windows: true })
                : Promise.resolve({ data: null, error: null }),
        ]);

        if (issuedFor.current !== key) return;

        if (summary.error) {
            setError('We could not load your day. Try again in a moment.');
            setLoading(false);
            return;
        }

        const summaryRow = (Array.isArray(summary.data) ? summary.data[0] : summary.data) as
            Record<string, unknown> | null;
        const chequeRow = (Array.isArray(cheques.data) ? cheques.data[0] : cheques.data) as
            Record<string, unknown> | null;

        setData({
            totals: readWorklistSummary(summaryRow),
            team: ((team.data ?? []) as Record<string, unknown>[]).map(row => ({
                memberId: String(row['member_id']),
                displayName: String(row['display_name'] ?? 'Somebody'),
                overdueCount: Number(row['overdue_count']) || 0,
                todayCount: Number(row['today_count']) || 0,
                urgentCount: Number(row['urgent_count']) || 0,
                openCount: Number(row['open_count']) || 0,
                openAmount: Number(row['open_amount']) || 0,
                futureCount: Number(row['future_count']) || 0,
                noFollowUpCount: Number(row['no_follow_up_count']) || 0,
                completedCount: Number(row['completed_count']) || 0,
                badDebtCount: Number(row['bad_debt_count']) || 0,
            })),
            ageing: ageing.error ? [] : readAgeingReport((ageing.data ?? []) as Record<string, unknown>[]),
            cheques: withCheques && !cheques.error && chequeRow
                ? {
                    matchedCount: Number(chequeRow['matched_count']) || 0,
                    matchedAmount: Number(chequeRow['matched_amount']) || 0,
                    visibleCount: Number(chequeRow['visible_count']) || 0,
                    byState: {
                        due: figure(chequeRow, 'due'),
                        overdue: figure(chequeRow, 'overdue'),
                        upcoming: figure(chequeRow, 'upcoming'),
                        hold: figure(chequeRow, 'hold'),
                        bounced: figure(chequeRow, 'bounced'),
                        cleared: figure(chequeRow, 'cleared'),
                    },
                }
                : withCheques ? emptyChequeTotals() : null,
            chequesUnavailable: withCheques && Boolean(cheques.error),
        });
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    useEffect(() => { void load(); }, [load]);

    return { ...data, loading, error, reload: load };
}

const figure = (row: Record<string, unknown>, state: string) => ({
    count: Number(row[`${state}_count`]) || 0,
    amount: Number(row[`${state}_amount`]) || 0,
});

/**
 * A manager's own share of each queue, for Today's "Mine" view: `book_totals`
 * under exactly the filter the queue's list uses, with `owner = mine`, so the
 * count beside a group and the list under it are the same question.
 */
export function useMineCounts(context: TenantContext, enabled: boolean): Record<WorklistGroup, WorklistFigure> | null {
    const workspaceId = context.workspace.id;
    const [counts, setCounts] = useState<Record<WorklistGroup, WorklistFigure> | null>(null);
    useEffect(() => {
        if (!enabled) { setCounts(null); return; }
        let current = true;
        void Promise.all(WORKLIST_GROUPS.map(async group => {
            const filters = { ...DEFAULT_FILTERS, ...group.filters, owner: 'mine' };
            const { data, error } = await supabase().rpc('book_totals', filterArgs(workspaceId, filters));
            const row = (Array.isArray(data) ? data[0] : data) as { matched_count?: number; outstanding?: number } | null;
            return [group.group, error || !row ? { count: 0, amount: 0 } : { count: Number(row.matched_count) || 0, amount: Number(row.outstanding) || 0 }] as const;
        })).then(entries => { if (current) setCounts(Object.fromEntries(entries) as Record<WorklistGroup, WorklistFigure>); });
        return () => { current = false; };
    }, [workspaceId, enabled]);
    return counts;
}

/**
 * The two counts the navigation carries (DESIGN-SYSTEM §10): overdue
 * follow-ups beside Today, and cheques to bank or chase beside Cheques.
 *
 * One small call, `nav_counts()` (0033), which counts exactly those two
 * things under the caller's own scope — `supabase/tests/27` holds each equal
 * to Today's and the register's own figure. It waits a moment after arrival
 * so the screen's own reads go first, and asks again when the window comes
 * back into use; never on every render. A failure shows no count rather than
 * a wrong one.
 */
export function useNavCounts(context: TenantContext, withCheques: boolean): { overdue: number | null; cheques: number | null } {
    const workspaceId = context.workspace.id;
    const [counts, setCounts] = useState<{ overdue: number | null; cheques: number | null }>({ overdue: null, cheques: null });
    useEffect(() => {
        let current = true;
        const read = () => {
            void supabase().rpc('nav_counts', { p_workspace: workspaceId }).then(({ data, error }) => {
                if (!current) return;
                const row = (Array.isArray(data) ? data[0] : data) as Record<string, unknown> | null;
                setCounts({
                    overdue: error || !row ? null : Number(row['overdue_count']) || 0,
                    cheques: !withCheques || error || !row ? null : Number(row['cheques_count']) || 0,
                });
            });
        };
        const first = window.setTimeout(read, NAV_COUNTS_DELAY);
        const onVisible = () => { if (document.visibilityState === 'visible') read(); };
        document.addEventListener('visibilitychange', onVisible);
        return () => { current = false; window.clearTimeout(first); document.removeEventListener('visibilitychange', onVisible); };
    }, [workspaceId, withCheques]);
    return counts;
}

/** How long the navigation waits before asking, so a screen's own reads go first. */
const NAV_COUNTS_DELAY = 600;
